import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, StepList, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function LadeboksInstallation({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad koster det at installere en ladeboks i 2026?', answer: 'Installation af en ladeboks koster typisk fra 1.300 kr. ved et eksisterende stik og fra 3.000 kr. ved nyt stik med kabelføring. Prisen afhænger af afstand til eltavlen, behov for ny gruppe og om der skal graves. Arbejdet skal udføres af en autoriseret elinstallatør.' },
    { question: 'Skal en ladeboks installeres af en autoriseret elinstallatør?', answer: 'Ja. En fast monteret ladeboks skal altid installeres af en autoriseret elinstallatør, både af sikkerhedshensyn og for at overholde dansk lovgivning. Det sikrer korrekt fejlstrømsafbryder (HPFI/RCD type B eller A-EV) og dimensionering.' },
    { question: 'Hvad påvirker prisen på installationen?', answer: 'De vigtigste faktorer er: afstanden fra eltavlen til ladeboksen, om der skal trækkes nyt kabel eller graves, om eltavlen skal opgraderes, om der er ledig kapacitet på din installation, og om der kræves en ny gruppe/sikring. Et kort, enkelt forløb er billigst.' },
    { question: 'Hvor lang tid tager en ladeboks-installation?', answer: 'En typisk standardinstallation tager 1–2 timer. Er der behov for kabelføring over lang afstand, gravearbejde eller opgradering af eltavlen, kan det tage længere tid.' },
    { question: 'Kan jeg få elafgift-refusion for hjemmeopladning?', answer: 'Ja. Som elbilejer kan du få refunderet en stor del af elafgiften på den strøm, du lader bilen med hjemme. Hos enkel energi søger vi automatisk refusionen for dig som en del af serviceaftalen – du skal ikke gøre noget.' },
    { question: 'Hvilken ladeboks bør jeg vælge?', answer: 'Til de fleste hjem er NexBlue Edge 2 (3.499 kr.) det billigste premium-valg, mens Zaptec Go 2 (5.999 kr.) er det mest fremtidssikrede med V2G. Begge leverer 22 kW og inkluderer Saving Mode og serviceaftale til 0 kr./md. hos enkel energi.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'priser', label: 'Priser' },
    { id: 'pavirker', label: 'Hvad påvirker prisen' },
    { id: 'forloeb', label: 'Sådan foregår det' },
    { id: 'boks', label: 'Vælg ladeboks' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: 'Installation af ladeboks', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Installation af ladeboks – pris og guide 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Guide · Installation 2026"
        h1="Installation af ladeboks: Pris og komplet guide 2026"
        intro="Hvad koster det at få installeret en ladeboks, hvad påvirker prisen, og hvordan foregår det? Få det fulde overblik – og se hvilken ladeboks der passer til dit hjem."
        badges={['Fra 1.300 kr.', 'Autoriseret installatør', 'Elafgift-refusion inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1558389186-438424b00a6b?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: 'Fra 1.300 kr.', label: 'eksisterende stik' },
          { value: 'Fra 3.000 kr.', label: 'nyt stik / kabelføring' },
          { value: '1–2 timer', label: 'typisk arbejdstid' },
          { value: '0 kr./md.', label: 'serviceaftale + refusion' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad koster installation af en ladeboks?">
        <AnswerBox question="Hvad koster det at installere en ladeboks i 2026?">
          Installation koster typisk <strong>fra 1.300 kr.</strong> ved et eksisterende stik og <strong>fra 3.000 kr.</strong> ved nyt stik
          med kabelføring. Prisen afhænger af afstand til eltavlen, behov for ny gruppe og eventuelt gravearbejde. Arbejdet skal udføres af
          en <strong>autoriseret elinstallatør</strong>. Hos enkel energi følger Saving Mode, serviceaftale og automatisk elafgift-refusion med til 0 kr./md.
        </AnswerBox>
        <p>
          En ladeboks-installation er en éngangsudgift, der hurtigt tjener sig hjem, fordi hjemmeopladning (AC) altid er billigere end offentlig
          DC-hurtigladning. Kombineret med spotpris uden tillæg og Saving Mode er din egen ladeboks den billigste måde at lade elbil på.
        </p>
      </ProseSection>

      <ProseSection id="priser" heading="Installationspriser 2026" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Typiske installationspriser"
            columns={[{ name: 'Pris fra', highlight: true }, { name: 'Hvornår' }]}
            rows={[
              { label: 'Eksisterende stik', values: ['1.300 kr.', 'Der er allerede et egnet udtag tæt på'] },
              { label: 'Standard installation', values: ['3.000 kr.', 'Nyt kabel fra eltavle, kort afstand'] },
              { label: 'Udvidet installation', values: ['Tilbud', 'Lang afstand, gravearbejde, tavle-opgradering'] },
            ]}
          />
        </div>
        <p>Priserne er vejledende. Du får altid en konkret pris ud fra dit hjem, inden arbejdet går i gang.</p>
      </ProseSection>

      <ProseSection id="pavirker" heading="Hvad påvirker prisen?">
        <FactGrid columns={2} facts={[
          { value: 'Afstand', label: 'Fra eltavle til ladeboks', sub: 'Længere kabel = højere pris' },
          { value: 'Gravearbejde', label: 'Skal der graves kabel ned?', sub: 'Fx til carport eller garage' },
          { value: 'Eltavle', label: 'Ledig kapacitet / ny gruppe', sub: 'Opgradering kan være nødvendig' },
          { value: 'Fejlstrøm', label: 'HPFI/RCD til elbil', sub: 'Krav for sikker opladning' },
        ]} />
        <p className="mt-6">
          Det billigste forløb er et kort, lige kabeltræk fra eltavlen til en ydervæg med plads til ladeboksen, hvor der er ledig kapacitet på
          installationen. Bor du i et nyere hus med 3-faset tilslutning, er det oftest ligetil.
        </p>
      </ProseSection>

      <ProseSection id="forloeb" heading="Sådan foregår en installation – trin for trin" alt>
        <StepList steps={[
          { title: 'Vælg ladeboks og el-aftale', body: 'Bestil din ladeboks hos enkel energi sammen med en el-aftale, så Saving Mode, serviceaftale og laderabat følger med.' },
          { title: 'Vurdering af din installation', body: 'Vi afdækker afstand, eltavle og behov for ny gruppe, så du får den rette pris.' },
          { title: 'Autoriseret elinstallatør monterer', body: 'En autoriseret installatør sætter boksen op, trækker kabel og sikrer korrekt fejlstrømsbeskyttelse. Tager typisk 1–2 timer.' },
          { title: 'Tilslut til enkel-appen', body: 'Forbind ladeboksen til enkel Saving Mode-appen, og opladningen optimeres automatisk til de billigste timer.' },
          { title: 'Automatisk refusion og rabat', body: 'Vi søger elafgift-refusion for dig, og du får 10 øre/kWh laderabat på de kWh, du lader via Saving Mode i enkel-appen (kræver kompatibel ladeboks og bil).' },
        ]} />
      </ProseSection>

      <ProseSection id="boks" heading="Hvilken ladeboks skal du vælge?">
        <p>Vi tilbyder tre udvalgte premium-ladebokse. Begge nedenfor er populære valg – det billigste og det mest fremtidssikrede:</p>
        <div className="grid sm:grid-cols-2 gap-4 my-6">
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.nexblue_edge_2} note="Billigste premium-valg" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go_2} note="Mest fremtidssikret · V2G" />
        </div>
        <p>
          Begge leverer 22 kW, virker med alle elbiler (Type 2) og inkluderer Saving Mode og serviceaftale til 0 kr./md. Se også
          {' '}<a href="/zaptec-go" className="text-[#0f5c4a] font-semibold underline">Zaptec Go</a> som et populært mellemvalg.
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/hvad-koster-installation-af-ladeboks', label: 'Pris på installation' },
        { path: '/hvilken-ladeboks-skal-jeg-vaelge', label: 'Hvilken ladeboks?' },
        { path: '/sammenlign/zaptec-go-vs-go-2', label: 'Zaptec Go vs Go 2' },
        { path: '/service-aftale', label: 'Serviceaftale' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Klar til din egen ladeboks?" subtext="Ladeboks fra 3.499 kr. + installation fra 1.300 kr. Saving Mode og serviceaftale inkluderet." primary={{ path: '/opladere', text: 'Se ladebokse' }} secondary={{ path: '/elaftale', text: 'Se el-aftaler' }} />
      <SEODisclaimer page={page} />
    </article>
  );
}